import React, { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    Typography,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Avatar,
    Menu,
    MenuItem,
    Breadcrumbs,
    Link,
    Badge,
    Tooltip,
    Paper,
    Chip,
    Button,
    CircularProgress,
    Collapse,
    ListItemButton,
} from '@mui/material';
import {
    Menu as MenuIcon,
    ChevronLeft as ChevronLeftIcon,
    Home as HomeIcon,
    AccountCircle,
    ViewKanban as ViewKanbanIcon,
    Close as CloseIcon,
    Dashboard as DashboardIcon,
    People as PeopleIcon,
    Straighten as MeasurementIcon,
    Assignment as ProjectIcon,
    Person as EmployeeIcon,
    Settings as SettingsIcon,
    AssignmentInd as AssignmentIcon,
    ManageAccounts as AdminIcon,
    EmojiEvents,
    Timeline as TimelineIcon,
    EventNote as EventNoteIcon,
    ExpandLess,
    ExpandMore,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../redux/slices/authSlice';
import { useAuth } from '../../hooks/useAuth';
import { AppDispatch, RootState } from '../../redux/store';
import ThemeToggle from '../common/ThemeToggle';
import AssignmentKanbanBoard from '../assignments/AssignmentKanbanBoard';
import { fetchAssignments } from '../../redux/slices/assignmentsSlice';
import { fetchStages } from '../../redux/slices/stagesSlice';
import { fetchEmployees } from '../../redux/slices/employeesSlice';
import { getMenuItemsForRole, getGroupedMenuItemsForRole, PERMISSIONS } from '../../utils/roleConfig';
import { preloadPageOnHover } from '../../utils/preloader';

const drawerWidth = 240;

// Local icon mapping to ensure proper rendering
const ICON_MAP_BY_PATH: Record<string, React.ComponentType> = {
    '/': DashboardIcon,
    '/clients': PeopleIcon,
    '/measurements': MeasurementIcon,
    '/projects': ProjectIcon,
    '/employees': EmployeeIcon,
    '/stages': SettingsIcon,
    '/assignments': AssignmentIcon,
    '/admin/assignments': AdminIcon,
    '/admin/employee-assignments': PeopleIcon,
    '/admin/time-reports': TimelineIcon,
    '/admin/attendance': EventNoteIcon,
    '/leaderboard': EmojiEvents,
};

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
    open?: boolean;
}>(({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    // Mobile-friendly: Reduce padding on small screens
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
    },
    transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    // Mobile-friendly: No negative margin on small screens
    [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
        padding: theme.spacing(1),
    },
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
    }),
}));

const AppBarStyled = styled(AppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<{
    open?: boolean;
}>(({ theme, open }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

const Layout: React.FC = () => {
    const [open, setOpen] = useState(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [showKanban, setShowKanban] = useState(false);
    const [kanbanFilter, setKanbanFilter] = useState<string | null>(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set(['main']));
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const { user, isLoading: authLoading } = useAuth();
    const { assignments, isLoading: assignmentsLoading } = useSelector((state: RootState) => state.assignments);
    const { stages, isLoading: stagesLoading } = useSelector((state: RootState) => state.stages);

    // Fetch data only when the kanban board is opened
    useEffect(() => {
        if (showKanban && !dataLoaded) {
            // Only load the data if it's not already loaded
            dispatch(fetchStages({ sort: 'order:asc', populate: '*' }));
            dispatch(fetchEmployees({ populate: '*' }));
            dispatch(fetchAssignments({
                page: 0,
                pageSize: 100,
                filters: {},
                populate: '*'
            }));
            setDataLoaded(true);
        }
    }, [dispatch, showKanban, dataLoaded]);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Alt+K to toggle kanban board
            if (e.altKey && e.key === 'k') {
                e.preventDefault();
                setShowKanban(prev => !prev);
            }

            // Escape key to close kanban board
            if (e.key === 'Escape' && showKanban) {
                setShowKanban(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showKanban]);

    // Count assignments by status - only calculate when Kanban is visible
    const assignmentCounts = showKanban ? {
        pending: assignments.filter(a => a.attributes?.status === 'pending').length,
        inProgress: assignments.filter(a => a.attributes?.status === 'in_progress').length,
        paused: assignments.filter(a => a.attributes?.status === 'paused').length,
        completed: assignments.filter(a => a.attributes?.status === 'completed').length,
    } : { pending: 0, inProgress: 0, paused: 0, completed: 0 };

    // Count pending assignments (including in_progress)
    const pendingAssignmentsCount = assignmentCounts.pending + assignmentCounts.inProgress;

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        dispatch(logout());
        handleClose();
        navigate('/login');
    };

    const toggleKanban = () => {
        setShowKanban(!showKanban);
    };

    // Calculate menu items and memoize them based on user role
    const menuItems = React.useMemo(() => {
        // Debug logging to track authentication state
        console.log('Layout: Calculating menu items', {
            authLoading,
            user: user ? {
                id: user.id,
                email: user.email,
                role: user.role
            } : null,
            roleName: user?.role?.name,
            roleObject: user?.role,
            timestamp: new Date().toISOString()
        });

        // If still loading auth, return employee menu as fallback to prevent flashing
        if (authLoading) {
            console.log('Layout: Returning employee menu - auth loading');
            return [
                { text: 'Assignments', icon: <AssignmentIcon />, path: '/assignments' },
            ];
        }

        // If no user, return employee menu
        if (!user) {
            console.log('Layout: Returning employee menu - no user');
            return [
                { text: 'Assignments', icon: <AssignmentIcon />, path: '/assignments' },
            ];
        }

        // If user exists but role is not yet loaded, return employee menu
        if (!user.role || !user.role.name) {
            console.log('Layout: Returning employee menu - role not loaded yet', { user: user, role: user.role });
            return [
                { text: 'Assignments', icon: <AssignmentIcon />, path: '/assignments' },
            ];
        }
        
        const items = getMenuItemsForRole(user.role.name);
        console.log('Layout: Generated menu items', { roleName: user.role.name, itemCount: items.length, items: items.map(i => i.text) });
        return items;
    }, [user?.role?.name, user?.role, authLoading, user, user?.id]);

    const groupedMenuItems = useMemo(() => {
        if (authLoading || !user) {
            return [];
        }
        
        const groups = getGroupedMenuItemsForRole(user.role?.name);
        console.log('Layout: Generated grouped menu items', { roleName: user.role?.name, groupCount: groups.length });
        return groups;
    }, [user?.role?.name, user?.role, authLoading, user, user?.id]);

    const toggleMenuExpansion = (menuKey: string) => {
        setExpandedMenus(prev => {
            const newSet = new Set(prev);
            if (newSet.has(menuKey)) {
                newSet.delete(menuKey);
            } else {
                newSet.add(menuKey);
            }
            return newSet;
        });
    };

    // Show loading spinner while authentication is being determined
    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    // Generate breadcrumbs based on current path
    const getBreadcrumbs = () => {
        const pathnames = location.pathname.split('/').filter((x) => x);

        return (
            <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1, mb: 3 }}>
                <Link
                    underline="hover"
                    sx={{ display: 'flex', alignItems: 'center' }}
                    color="inherit"
                    href="/"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/');
                    }}
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Home
                </Link>
                {pathnames.map((name, index) => {
                    const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
                    const isLast = index === pathnames.length - 1;

                    return isLast ? (
                        <Typography key={name} color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                            {name.charAt(0).toUpperCase() + name.slice(1)}
                        </Typography>
                    ) : (
                        <Link
                            underline="hover"
                            key={name}
                            color="inherit"
                            href={routeTo}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(routeTo);
                            }}
                        >
                            {name.charAt(0).toUpperCase() + name.slice(1)}
                        </Link>
                    );
                })}
            </Breadcrumbs>
        );
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBarStyled position="fixed" open={open}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{ mr: 2, ...(open && { display: 'none' }) }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        Silhouette by Neha
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title="Toggle Kanban Board (Alt+K)">
                            <IconButton color="inherit" onClick={toggleKanban}>
                                <Badge badgeContent={dataLoaded ? pendingAssignmentsCount : 0} color="error">
                                    <ViewKanbanIcon />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                        <ThemeToggle />
                        <IconButton
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleMenu}
                            color="inherit"
                        >
                            <AccountCircle />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                        >
                            <MenuItem disabled>
                                {user?.username} ({user?.role?.name})
                            </MenuItem>
                            <MenuItem onClick={handleLogout}>Logout</MenuItem>
                        </Menu>
                    </div>
                </Toolbar>
            </AppBarStyled>

            {/* Kanban Board Overlay with data loading */}
            {showKanban && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1300,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 2,
                    }}
                    onClick={() => setShowKanban(false)}
                >
                    <Paper
                        sx={{
                            width: '95%',
                            height: '90%',
                            maxWidth: 1600,
                            maxHeight: 900,
                            overflow: 'hidden',
                            borderRadius: 2,
                            position: 'relative',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Box sx={{
                            p: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: 1,
                            borderColor: 'divider'
                        }}>
                            <Box>
                                <Typography variant="h6">Assignment Kanban Board</Typography>
                                <Box sx={{ display: 'flex', mt: 1, gap: 1 }}>
                                    <Tooltip title="All assignments">
                                        <Chip
                                            label={dataLoaded ? `All: ${assignments.length}` : 'All: Loading...'}
                                            onClick={() => setKanbanFilter(null)}
                                            color={kanbanFilter === null ? 'primary' : 'default'}
                                            variant={kanbanFilter === null ? 'filled' : 'outlined'}
                                            size="small"
                                        />
                                    </Tooltip>
                                    <Tooltip title="Pending assignments">
                                        <Chip
                                            label={dataLoaded ? `Pending: ${assignmentCounts.pending}` : 'Pending: ...'}
                                            onClick={() => setKanbanFilter('pending')}
                                            color={kanbanFilter === 'pending' ? 'primary' : 'default'}
                                            variant={kanbanFilter === 'pending' ? 'filled' : 'outlined'}
                                            size="small"
                                        />
                                    </Tooltip>
                                    <Tooltip title="In progress assignments">
                                        <Chip
                                            label={dataLoaded ? `In Progress: ${assignmentCounts.inProgress}` : 'In Progress: ...'}
                                            onClick={() => setKanbanFilter('in_progress')}
                                            color={kanbanFilter === 'in_progress' ? 'primary' : 'default'}
                                            variant={kanbanFilter === 'in_progress' ? 'filled' : 'outlined'}
                                            size="small"
                                        />
                                    </Tooltip>
                                    <Tooltip title="Paused assignments">
                                        <Chip
                                            label={dataLoaded ? `Paused: ${assignmentCounts.paused}` : 'Paused: ...'}
                                            onClick={() => setKanbanFilter('paused')}
                                            color={kanbanFilter === 'paused' ? 'primary' : 'default'}
                                            variant={kanbanFilter === 'paused' ? 'filled' : 'outlined'}
                                            size="small"
                                        />
                                    </Tooltip>
                                </Box>
                            </Box>
                            <Box>
                                <Tooltip title="Go to Admin Assignments Page">
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        onClick={() => {
                                            navigate('/admin/assignments');
                                            setShowKanban(false);
                                        }}
                                        sx={{ mr: 2 }}
                                    >
                                        Admin View
                                    </Button>
                                </Tooltip>
                                <IconButton onClick={() => setShowKanban(false)} size="small">
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>
                        <Box sx={{ height: 'calc(100% - 100px)', overflow: 'auto', p: 2 }}>
                            {!dataLoaded || assignmentsLoading || stagesLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
                                    <CircularProgress />
                                    <Typography variant="body2" color="text.secondary">
                                        Loading assignment data...
                                    </Typography>
                                </Box>
                            ) : (
                                <AssignmentKanbanBoard
                                    statusFilter={kanbanFilter}
                                    onViewAssignment={(assignmentId) => {
                                        navigate(`/assignments/${assignmentId}`);
                                        setShowKanban(false);
                                    }}
                                />
                            )}
                        </Box>
                        <Box sx={{
                            p: 2,
                            borderTop: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                        }}>
                            <Typography variant="caption" color="text.secondary">
                                Tip: Use Alt+K to quickly open/close the kanban board
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {dataLoaded ? 
                                    `Total Stages: ${stages.length} | Total Assignments: ${assignments.length}` :
                                    'Loading data...'
                                }
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            )}

            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                    // Mobile-friendly: Hide drawer on small screens when closed
                    display: { xs: open ? 'block' : 'none', sm: 'block' },
                }}
                variant="persistent"
                anchor="left"
                open={open}
            >
                <DrawerHeader>
                    <Avatar sx={{ mr: 2 }}>SN</Avatar>
                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        Silhouette by Neha
                    </Typography>
                    <IconButton onClick={handleDrawerClose}>
                        <ChevronLeftIcon />
                    </IconButton>
                </DrawerHeader>
                <Divider />
                <List>
                    {groupedMenuItems.map((group) => (
                        <React.Fragment key={group.key}>
                            <ListItemButton onClick={() => toggleMenuExpansion(group.key)}>
                                <ListItemIcon>
                                    <group.icon />
                                </ListItemIcon>
                                <ListItemText primary={group.title} />
                                {expandedMenus.has(group.key) ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                            <Collapse in={expandedMenus.has(group.key)} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {group.items.map((item, index) => {
                                        const IconComponent = item.icon;
                                        
                                        return (
                                            <ListItemButton
                                                key={`${item.text}-${index}`}
                                                sx={{ pl: 4 }}
                                                onClick={() => navigate(item.path)}
                                                selected={location.pathname === item.path}
                                                onMouseEnter={() => {
                                                    // Map path to page name for preloading
                                                    const pageName = item.path.split('/').filter(p => p).join('-') || 'dashboard';
                                                    preloadPageOnHover(pageName);
                                                }}
                                            >
                                                <ListItemIcon>
                                                    <IconComponent />
                                                </ListItemIcon>
                                                <ListItemText primary={item.text} />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    ))}
                </List>
            </Drawer>
            <Main open={open}>
                <DrawerHeader />
                {getBreadcrumbs()}
                <Outlet />
            </Main>
        </Box>
    );
};

export default Layout; 